import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import BottomLine from "~/src/components/BottomLine";
import BoldText from "~/src/components/Text/BoldText";
import elgEttårigLangt from "/img/aldersbestemmelse/Kjeveillustrasjon_Elg_ettåring_langt_utviklet.png";
import elgEttårigLite from "/img/aldersbestemmelse/Kjeveillustrasjon_Elg_ettåring_lite_utviklet.png";
import elgKalv from "/img/aldersbestemmelse/Kjeveillustrasjon_Elg_kalv.png";
import elgEldre from "/img/aldersbestemmelse/Kjeveillustrasjon_Elg_voksen.png";
import hjortEtOgEtHalvt from "/img/aldersbestemmelse/Kjeveillustrasjon_Hjort_villrein_ettåring.png";
import hjortHalvt from "/img/aldersbestemmelse/Kjeveillustrasjon_Hjort_villrein_kalv.png";
import hjortToOgEtHalvtLite from "/img/aldersbestemmelse/Kjeveillustrasjon_Hjort_villrein_toåring-03.png";
import hjortToOgEtHalvtLangt from "/img/aldersbestemmelse/Kjeveillustrasjon_Hjort_villrein_toåring-05.png";
import hjortEldre from "/img/aldersbestemmelse/Kjeveillustrasjon_Hjort_voksen.png";
import { AccordionDefault } from "../../components/Accordion/AccordionDefault";
import { ArtType } from "../../helpers/types";

function Aldersbestemmelse(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const valgtArt: ArtType = location.state?.art;
  const textStyle = "m-4 mb-0 text-lg";
  const imageStyle = "p-5 m-auto min-w-[80%]";

  return (
    <div className="m-4 mt-0 pb-6 text-ldir-brown-900 text-lg">
      <h2 className="text-2xl font-serif font-bold py-4">
        {t("pages.aldersbestemmelse.subtitle")}
      </h2>
      {(valgtArt === ArtType.Elg || valgtArt === undefined) && (
        <>
          <AccordionDefault
            defaultOpen={false}
            cardColor="bg-white"
            title={t("pages.aldersbestemmelse.elg.kalv.title")}
          >
            <img
              alt={t("pages.aldersbestemmelse.elg.kalv.alttext")}
              src={elgKalv}
              className={imageStyle}
            />
            <BoldText
              className={textStyle}
              text={t("pages.aldersbestemmelse.elg.kalv.text")}
              numBoldWords={3}
            />
          </AccordionDefault>

          <AccordionDefault
            defaultOpen={false}
            cardColor="bg-white"
            title={t("pages.aldersbestemmelse.elg.lite.title")}
          >
            <img
              alt={t("pages.aldersbestemmelse.elg.lite.alttext")}
              src={elgEttårigLite}
              className={imageStyle}
            />
            <BoldText
              className={textStyle}
              text={t("pages.aldersbestemmelse.elg.lite.text")}
              numBoldWords={3}
            />
          </AccordionDefault>

          <AccordionDefault
            defaultOpen={false}
            cardColor="bg-white"
            title={t("pages.aldersbestemmelse.elg.langt.title")}
          >
            <img
              alt={t("pages.aldersbestemmelse.elg.langt.alttext")}
              src={elgEttårigLangt}
              className={imageStyle}
            />
            <BoldText
              className={textStyle}
              text={t("pages.aldersbestemmelse.elg.langt.text")}
              numBoldWords={3}
            />
          </AccordionDefault>

          <AccordionDefault
            defaultOpen={false}
            cardColor="bg-white"
            title={t("pages.aldersbestemmelse.elg.eldre.title")}
          >
            <img
              alt={t("pages.aldersbestemmelse.elg.eldre.alttext")}
              src={elgEldre}
              className={imageStyle}
            />
            <BoldText
              className={textStyle}
              text={t("pages.aldersbestemmelse.elg.eldre.text")}
              numBoldWords={8}
            />
          </AccordionDefault>
        </>
      )}
      {valgtArt === undefined && <BottomLine className="border-b-4" />}
      {(valgtArt === ArtType.Hjort || valgtArt === undefined) && (
        <>
          <AccordionDefault
            defaultOpen={false}
            cardColor="bg-white"
            title={t("pages.aldersbestemmelse.hjort.halvt.title")}
          >
            <img
              alt={t("pages.aldersbestemmelse.hjort.halvt.alttext")}
              src={hjortHalvt}
              className={imageStyle}
            />
            <BoldText
              className={textStyle}
              text={t("pages.aldersbestemmelse.hjort.halvt.text")}
              numBoldWords={5}
            />
          </AccordionDefault>
          <AccordionDefault
            defaultOpen={false}
            cardColor="bg-white"
            title={t("pages.aldersbestemmelse.hjort.etOgEtHalvt.title")}
          >
            <img
              alt={t("pages.aldersbestemmelse.hjort.etOgEtHalvt.alttext")}
              src={hjortEtOgEtHalvt}
              className={imageStyle}
            />
            <BoldText
              className={textStyle}
              text={t("pages.aldersbestemmelse.hjort.etOgEtHalvt.text")}
              numBoldWords={3}
            />
          </AccordionDefault>

          <AccordionDefault
            defaultOpen={false}
            cardColor="bg-white"
            title={t("pages.aldersbestemmelse.hjort.toOgEtHalvt.title")}
          >
            <img
              alt={t("pages.aldersbestemmelse.hjort.toOgEtHalvt.alttext")}
              src={hjortToOgEtHalvtLite}
              className={imageStyle}
            />
            <img
              alt={t("pages.aldersbestemmelse.hjort.toOgEtHalvt.alttext")}
              src={hjortToOgEtHalvtLangt}
              className={imageStyle}
            />
            <BoldText
              className={textStyle}
              text={t("pages.aldersbestemmelse.hjort.toOgEtHalvt.text")}
              numBoldWords={5}
            />
          </AccordionDefault>

          <AccordionDefault
            defaultOpen={false}
            cardColor="bg-white"
            title={t("pages.aldersbestemmelse.hjort.treOgEtHalvtOgEldre.title")}
          >
            <img
              alt={t("pages.aldersbestemmelse.hjort.treOgEtHalvtOgEldre.alttext")}
              src={hjortEldre}
              className={imageStyle}
            />
            <BoldText
              className={textStyle}
              text={t("pages.aldersbestemmelse.hjort.treOgEtHalvtOgEldre.text")}
              numBoldWords={8}
            />
          </AccordionDefault>
        </>
      )}
    </div>
  );
}

export default Aldersbestemmelse;
